import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AdminLayout } from "../../components/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Shield, Users, ChevronLeft, ChevronRight, FileText, UserPlus, Trash2 } from "lucide-react";
import adminApi from "../../lib/adminApi";
import { toast } from "sonner";

const EVENT_TYPES = ["auth", "vote", "consulta", "scrutiny", "census", "export", "admin", "error"];
const RESULTS = ["success", "failure", "denied"];

function LogsTab() {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ event_type: "", result: "", date_from: "", date_to: "", consulta_id: "" });
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 25);
      if (filters.event_type) params.append("event_type", filters.event_type);
      if (filters.result) params.append("result", filters.result);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (filters.consulta_id) params.append("consulta_id", filters.consulta_id);
      const { data } = await adminApi.get(`/admin/logs?${params.toString()}`);
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      toast.error(t("audit.errorLogs"));
    } finally {
      setLoading(false);
    }
  }, [page, filters, t]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const resultBadge = (r) => {
    const cls = r === "success" ? "bg-emerald-900/50 text-emerald-300" : r === "failure" ? "bg-red-900/50 text-red-300" : "bg-amber-900/50 text-amber-300";
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{r}</span>;
  };

  const eventBadge = (e) => {
    const colors = { auth: "bg-blue-900/50 text-blue-300", vote: "bg-purple-900/50 text-purple-300", consulta: "bg-teal-900/50 text-teal-300", admin: "bg-orange-900/50 text-orange-300", scrutiny: "bg-pink-900/50 text-pink-300" };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[e] || "bg-slate-700 text-slate-300"}`}>{e}</span>;
  };

  const locale = i18n.language || "ca";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-slate-400">{t("audit.filterType")}</Label>
          <select value={filters.event_type} onChange={(e) => { setFilters({...filters, event_type: e.target.value}); setPage(1); }}
            className="block mt-1 bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-slate-200" data-testid="log-filter-type">
            <option value="">{t("audit.filterAll")}</option>
            {EVENT_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("audit.filterResult")}</Label>
          <select value={filters.result} onChange={(e) => { setFilters({...filters, result: e.target.value}); setPage(1); }}
            className="block mt-1 bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-slate-200" data-testid="log-filter-result">
            <option value="">{t("audit.filterAll")}</option>
            {RESULTS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("audit.filterFrom")}</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => { setFilters({...filters, date_from: e.target.value}); setPage(1); }}
            className="mt-1 bg-slate-800 border-slate-600 text-slate-200 h-8" data-testid="log-filter-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("audit.filterTo")}</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => { setFilters({...filters, date_to: e.target.value}); setPage(1); }}
            className="mt-1 bg-slate-800 border-slate-600 text-slate-200 h-8" data-testid="log-filter-to" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("audit.filterConsulta")}</Label>
          <Input value={filters.consulta_id} onChange={(e) => { setFilters({...filters, consulta_id: e.target.value}); setPage(1); }}
            placeholder="ID..." className="mt-1 bg-slate-800 border-slate-600 text-slate-200 h-8 w-48" data-testid="log-filter-consulta" />
        </div>
        <Button size="sm" variant="outline" onClick={() => { setFilters({ event_type: "", result: "", date_from: "", date_to: "", consulta_id: "" }); setPage(1); }}
          className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="log-filter-clear">
          {t("audit.filterClear")}
        </Button>
      </div>

      <div className="text-xs text-slate-400 mb-1">{t("audit.recordsFound", { count: total })}</div>

      <div className="overflow-x-auto rounded border border-slate-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-800 text-slate-300 text-xs uppercase">
            <tr>
              <th className="px-3 py-2">{t("audit.colDate")}</th>
              <th className="px-3 py-2">{t("audit.colType")}</th>
              <th className="px-3 py-2">{t("audit.colAction")}</th>
              <th className="px-3 py-2">{t("audit.colActor")}</th>
              <th className="px-3 py-2">{t("audit.colResult")}</th>
              <th className="px-3 py-2">{t("audit.colIP")}</th>
              <th className="px-3 py-2">{t("audit.colDetails")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {loading ? (
              <tr><td colSpan="7" className="px-3 py-8 text-center text-slate-400">{t("audit.loading")}</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan="7" className="px-3 py-8 text-center text-slate-400">{t("audit.noRecords")}</td></tr>
            ) : logs.map((log) => (
              <tr key={log.id} className="hover:bg-slate-800/50">
                <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">
                  {new Date(log.timestamp).toLocaleString(locale)}
                </td>
                <td className="px-3 py-2">{eventBadge(log.event_type)}</td>
                <td className="px-3 py-2 text-slate-200 font-mono text-xs">{log.action}</td>
                <td className="px-3 py-2 text-slate-300 text-xs">
                  <span className="text-slate-400">{log.actor_type}</span>
                  {log.actor_role && log.actor_role !== log.actor_type && <span className="ml-1 text-slate-500">({log.actor_role})</span>}
                </td>
                <td className="px-3 py-2">{resultBadge(log.result)}</td>
                <td className="px-3 py-2 text-slate-400 text-xs font-mono">{log.ip_address || "-"}</td>
                <td className="px-3 py-2 text-slate-400 text-xs max-w-[200px] truncate">
                  {log.extra ? JSON.stringify(log.extra) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="border-slate-600 text-slate-300" data-testid="logs-prev">
            <ChevronLeft className="h-4 w-4 mr-1" /> {t("audit.prev")}
          </Button>
          <span className="text-sm text-slate-400">{t("audit.pageOf", { page, pages })}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}
            className="border-slate-600 text-slate-300" data-testid="logs-next">
            {t("audit.next")} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

function AdminsTab() {
  const { t, i18n } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", role: "admin", email: "" });
  const [creating, setCreating] = useState(false);

  const locale = i18n.language || "ca";

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/admins");
      setAdmins(data);
    } catch {
      toast.error(t("audit.errorAdmins"));
    }
  }, [t]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await adminApi.post("/admin/admins", newAdmin);
      toast.success(t("audit.adminCreated"));
      setShowCreate(false);
      setNewAdmin({ username: "", password: "", role: "admin", email: "" });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("audit.errorCreate"));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(t("audit.confirmDelete", { name }))) return;
    try {
      await adminApi.delete(`/admin/admins/${id}`);
      toast.success(t("audit.adminDeleted"));
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("audit.errorDelete"));
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await adminApi.put(`/admin/admins/${id}/role`, { role: newRole });
      toast.success(t("audit.roleUpdated"));
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("audit.errorRole"));
    }
  };

  const roleBadge = (role) => {
    return role === "superadmin"
      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-900/50 text-amber-300">SuperAdmin</span>
      : <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300">Admin</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-400">{t("audit.adminCount", { count: admins.length })}</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-admin-btn"
          className="bg-emerald-600 hover:bg-emerald-700">
          <UserPlus className="h-4 w-4 mr-2" /> {t("audit.newAdmin")}
        </Button>
      </div>

      <div className="overflow-x-auto rounded border border-slate-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-800 text-slate-300 text-xs uppercase">
            <tr>
              <th className="px-4 py-2">{t("audit.colName")}</th>
              <th className="px-4 py-2">{t("audit.colEmail")}</th>
              <th className="px-4 py-2">{t("audit.colRole")}</th>
              <th className="px-4 py-2">{t("audit.colCreated")}</th>
              <th className="px-4 py-2">{t("audit.colActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {admins.map((a) => (
              <tr key={a.id} className="hover:bg-slate-800/50">
                <td className="px-4 py-2.5 text-slate-200">{a.name}</td>
                <td className="px-4 py-2.5 text-slate-300 text-xs font-mono">{a.email}</td>
                <td className="px-4 py-2.5">{roleBadge(a.role)}</td>
                <td className="px-4 py-2.5 text-slate-400 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString(locale) : "-"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-2">
                    <select value={a.role} onChange={(e) => handleRoleChange(a.id, e.target.value)}
                      className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200"
                      data-testid={`role-select-${a.id}`}>
                      <option value="admin">Admin</option>
                      <option value="superadmin">SuperAdmin</option>
                    </select>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(a.id, a.name)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-7 w-7 p-0"
                      data-testid={`delete-admin-${a.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-700" data-testid="create-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">{t("audit.newAdmin")}</DialogTitle>
            <DialogDescription className="text-slate-400">{t("audit.newAdminDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-slate-300">{t("audit.username")}</Label>
              <Input value={newAdmin.username} onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                className="bg-slate-800 border-slate-600 text-slate-200" data-testid="new-admin-username" />
            </div>
            <div>
              <Label className="text-slate-300">{t("audit.email")} <span className="text-slate-500 text-xs font-normal">{t("audit.emailOptional")}</span></Label>
              <Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                placeholder={`${newAdmin.username || "user"}@blockchaintools.es`}
                className="bg-slate-800 border-slate-600 text-slate-200" data-testid="new-admin-email" />
              <p className="text-xs text-slate-500 mt-1">{t("audit.emailHint")}</p>
            </div>
            <div>
              <Label className="text-slate-300">{t("audit.password")}</Label>
              <Input type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                className="bg-slate-800 border-slate-600 text-slate-200" data-testid="new-admin-password" />
            </div>
            <div>
              <Label className="text-slate-300">{t("audit.role")}</Label>
              <select value={newAdmin.role} onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-slate-200" data-testid="new-admin-role">
                <option value="admin">Admin</option>
                <option value="superadmin">SuperAdmin</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-slate-600 text-slate-300">{t("audit.cancel")}</Button>
            <Button onClick={handleCreate} disabled={creating || !newAdmin.username || !newAdmin.password}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-admin-submit">
              {creating ? t("audit.creating") : t("audit.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminAuditPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("logs");

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-audit-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-emerald-400" />
            <div>
              <h1 className="text-2xl font-bold text-slate-100">{t("audit.title")}</h1>
              <p className="text-sm text-slate-400">{t("audit.subtitle")}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-700 pb-1">
          <button onClick={() => setTab("logs")}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${tab === "logs" ? "bg-slate-800 text-emerald-400 border-b-2 border-emerald-400" : "text-slate-400 hover:text-slate-200"}`}
            data-testid="tab-logs">
            <FileText className="h-4 w-4 inline mr-2" />{t("audit.tabLogs")}
          </button>
          <button onClick={() => setTab("admins")}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${tab === "admins" ? "bg-slate-800 text-emerald-400 border-b-2 border-emerald-400" : "text-slate-400 hover:text-slate-200"}`}
            data-testid="tab-admins">
            <Users className="h-4 w-4 inline mr-2" />{t("audit.tabAdmins")}
          </button>
        </div>

        <Card className="bg-slate-900/50 border-slate-700">
          <CardContent className="pt-6">
            {tab === "logs" ? <LogsTab /> : <AdminsTab />}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
